import { HpDateValidator, HpIntegerValidator } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { HpQuestionDataTypes, HpTokensAndExpressionsThisReference, toUtcDate } from '@avantia/questionset-model';
import { objectToErrorOutput } from '../../tools/clientAndServerUtilities';
import {
  HpClientQuestionLookupItem,
  HpClientQuestionLookupItemMap,
  HpClientQuestionUI,
  HpControlValueTypes
} from '../../types';

export type HpEqualityComparer = (key: string, value: HpControlValueTypes) => boolean;

export function createEqualityComparer(
  dataType: HpQuestionDataTypes,
  isMultiSelect: boolean | undefined,
  questionId: string
): HpEqualityComparer {
  let func: HpEqualityComparer;
  if ((dataType === 'array' && !isMultiSelect) || (isMultiSelect && dataType !== 'array')) {
    throw new Error(
      `Questions with ${objectToErrorOutput({ dataType })} must also have ${objectToErrorOutput({
        isMultiSelect: true
      })} (QuestionID: '${questionId}').`
    );
  }

  if (dataType === 'array' && isMultiSelect === true) {
    func = (k, v): boolean => {
      return lodash.isArray(v) && v.indexOf(k) >= 0;
    };
  } else {
    func = (k, v): boolean => k === v;
  }

  return func;
}

export function iterateLookupMap(
  lookup: HpClientQuestionLookupItemMap,
  ui: HpClientQuestionUI,
  action: (item: HpClientQuestionLookupItem | null, key: string, index: number) => void
): void {
  const visibility = ui.lookupVisibility;
  let index = 0;
  lodash.forOwn(lookup, (item, key) => {
    if (!visibility || visibility[key]) {
      action(item, key, index++);
    }
  });
}

export type HpMinMaxValueTypes = Date | number;

export interface HpGetMinMaxValuesFromValidatorProps<ValueT extends HpMinMaxValueTypes> {
  minValue: ValueT | null;
  maxValue: ValueT | null;
}

export function getMinMaxDateValuesFromValidator(
  validator: HpDateValidator | undefined
): HpGetMinMaxValuesFromValidatorProps<Date> {
  return getMinMaxValuesFromValidator<Date>(validator, toUtcDate);
}

function getMinMaxValuesFromValidator<ValueT extends HpMinMaxValueTypes>(
  validator: HpDateValidator | HpIntegerValidator | undefined,
  convertToType: (value: any) => ValueT
): HpGetMinMaxValuesFromValidatorProps<ValueT> {
  if (!validator) {
    return { minValue: null, maxValue: null };
  }

  const props = validator.getProps();

  // eslint-disable-next-line prefer-const
  let { maxValue, maxValueExpr, minValue, minValueExpr } = props;

  if (minValueExpr || maxValueExpr) {
    const thisRef = new HpTokensAndExpressionsThisReference();
    if (minValueExpr) {
      minValue = (minValueExpr as (this: HpTokensAndExpressionsThisReference) => ValueT).call(thisRef);
    }

    if (maxValueExpr) {
      maxValue = (maxValueExpr as (this: HpTokensAndExpressionsThisReference) => ValueT).call(thisRef);
    }
  }

  return {
    minValue: minValue === undefined ? null : convertToType(minValue),
    maxValue: maxValue === undefined ? null : convertToType(maxValue)
  };
}
